import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { Checkbox, Form, Modal, Switch } from 'antd';
import { complianceDocumentsRestCrudThunks } from '../../thunks/compliance_documents_thunks';
import { fetchComplianceClientStatuses } from '../../thunks/compliance_statuses_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ComplianceDocument } from '../../types/ComplianceTypes';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';

interface ManageDocumentClientsModalProps {
	visible: boolean;
	complianceDocument: ComplianceDocument;
	onCancel: () => void;
	onSuccess: () => void;
	saving: boolean;
	fetchClientStatuses: () => Promise<any[]>;
	updateComplianceDocument: (entity: any) => Promise<any>;
}

const ManageDocumentClientsModal: FC<ManageDocumentClientsModalProps> = ({
	visible,
	complianceDocument,
	onCancel,
	onSuccess,
	saving,
	fetchClientStatuses,
	updateComplianceDocument,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [clients, setClients] = useState([]);

	// Clients (customers) come from the compliance client_statuses list
	// (buyerCompanyId + buyerCompanyName).
	useMountEffect(() => {
		fetchClientStatuses().then((clientStatuses) => setClients(clientStatuses || []));
	});

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			// "Select all" expands to the explicit list of every client's buyer-company
			// id — there is no all-clients flag; buyerCompanyIds is always the concrete
			// pre-authorization list.
			const buyerCompanyIds = values.shareWithAllClients
				? clients.map((client) => client.buyerCompanyId)
				: nullSafeGetOrElse('buyerCompanyIds', values, []);
			updateComplianceDocument({
				...complianceDocument,
				buyerCompanyIds,
			}).then(onSuccess);
		});
	};

	const clientOptions = clients.map((client) => ({
		label: nullSafeGetOrElse('buyerCompanyName', client, ''),
		value: client.buyerCompanyId,
	}));

	return (
		<Modal
			visible={visible}
			title="Add / Remove Client(s)"
			okText="APPLY"
			cancelText="CANCEL"
			onOk={handleSubmit}
			onCancel={onCancel}
			confirmLoading={saving}
			destroyOnClose={true}
			width={548}
		>
			<div className="mb-4 text-character-secondary">
				Clients added will automatically get access to "{complianceDocument.name}" if they request
				it.
			</div>
			<Form
				form={form}
				layout="vertical"
				preserve={false}
				initialValues={{
					shareWithAllClients: false,
					buyerCompanyIds: nullSafeGetOrElse('buyerCompanyIds', complianceDocument, []),
				}}
			>
				<div className="mb-2 flex flex-row items-center justify-between">
					<span className="bodymedium">Clients</span>
					<span className="flex flex-row items-center gap-2">
						<span className="text-character-secondary">Select all</span>
						<Form.Item name="shareWithAllClients" valuePropName="checked" noStyle={true}>
							<Switch size="small" />
						</Form.Item>
					</span>
				</div>
				<Form.Item noStyle={true} shouldUpdate={true}>
					{() =>
						form.getFieldValue('shareWithAllClients') ? (
							<div className="text-character-secondary">
								This document will be shared with every client in your list.
							</div>
						) : (
							<Form.Item name="buyerCompanyIds">
								<Checkbox.Group options={clientOptions} className="flex flex-col gap-2" />
							</Form.Item>
						)
					}
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	saving: state.compliance_documents.updating,
});

const mapDispatchToProps = (dispatch) => ({
	fetchClientStatuses: () => dispatch(fetchComplianceClientStatuses(ROLE_TYPES.SUPPLIER)()),
	updateComplianceDocument: (entity) =>
		dispatch(complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).update(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManageDocumentClientsModal);
